const SSO_URL = process.env.REACT_APP_SSO_URL || 'http://localhost:9000';

export interface Branding {
    logoUrl: string | null;
    primaryColor: string;
    secondaryColor: string;
    faviconUrl: string | null;
    loginBackgroundUrl: string | null;
    domain: string | null;
    appTitle: string | null;
    poweredByText: string | null;
}

export interface BrandingResponse {
    branding: Branding;
    companyName: string;
    found: boolean;
}

const DEFAULT_BRANDING: Branding = {
    logoUrl: null,
    primaryColor: '#1976d2',
    secondaryColor: '#dc004e',
    faviconUrl: null,
    loginBackgroundUrl: null,
    domain: null,
    appTitle: null,
    poweredByText: null,
};

class BrandingService {
    private cachedBranding: Branding | null = null;
    private cachedCompanyName: string | null = null;

    /**
     * Get branding by domain (for login page)
     */
    async getBrandingByDomain(domain?: string): Promise<BrandingResponse> {
        try {
            // window.location.host includes port (e.g. "localhost:3000"),
            // while hostname strips it — use host so stored domains with ports match.
            const targetDomain = domain || window.location.host;
            const response = await fetch(`${SSO_URL}/api/public/branding?domain=${encodeURIComponent(targetDomain)}`);

            if (!response.ok) {
                return {
                    branding: DEFAULT_BRANDING,
                    companyName: 'Online Academy',
                    found: false,
                };
            }

            const data = await response.json();

            // Cache the branding
            this.cachedBranding = data.branding;
            this.cachedCompanyName = data.companyName;

            return data;
        } catch (error) {
            console.error('Error fetching branding by domain:', error);
            return {
                branding: DEFAULT_BRANDING,
                companyName: 'Online Academy',
                found: false,
            };
        }
    }

    /**
     * Get branding by company ID (after login, from JWT token)
     */
    async getBrandingByCompanyId(companyId: number, token: string): Promise<BrandingResponse> {
        try {
            const response = await fetch(`${SSO_URL}/api/branding/${companyId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                return {
                    branding: DEFAULT_BRANDING,
                    companyName: 'Online Academy',
                    found: false,
                };
            }

            const data = await response.json();

            // Cache the branding
            this.cachedBranding = data.branding;
            this.cachedCompanyName = data.companyName;

            // Store in localStorage for persistence
            localStorage.setItem('app_branding', JSON.stringify(data.branding));
            localStorage.setItem('app_company_name', data.companyName);

            return data;
        } catch (error) {
            console.error('Error fetching branding by company ID:', error);
            return {
                branding: DEFAULT_BRANDING,
                companyName: 'Online Academy',
                found: false,
            };
        }
    }

    /**
     * Get cached branding or load from localStorage
     */
    getCachedBranding(): Branding {
        if (this.cachedBranding) {
            return this.cachedBranding;
        }

        const stored = localStorage.getItem('app_branding');
        if (stored) {
            try {
                this.cachedBranding = JSON.parse(stored);
                return this.cachedBranding!;
            } catch {
                // Invalid JSON, use default
            }
        }

        return DEFAULT_BRANDING;
    }

    /**
     * Get cached company name
     */
    getCachedCompanyName(): string {
        if (this.cachedCompanyName) {
            return this.cachedCompanyName;
        }

        return localStorage.getItem('app_company_name') || 'Online Academy';
    }

    /**
     * Clear cached branding (on logout)
     */
    clearCache(): void {
        this.cachedBranding = null;
        this.cachedCompanyName = null;
        localStorage.removeItem('app_branding');
        localStorage.removeItem('app_company_name');
    }

    /**
     * Update favicon dynamically
     */
    updateFavicon(faviconUrl: string | null): void {
        if (!faviconUrl) return;

        let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
        if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
        }
        link.href = faviconUrl.startsWith('/') ? `${SSO_URL}${faviconUrl}` : faviconUrl;
    }

    /**
     * Get default branding
     */
    getDefaultBranding(): Branding {
        return DEFAULT_BRANDING;
    }

    // ── Schreib-Methoden (2026-09 aus dem Admin hierher gezogen — Branding
    //    wird seit dem azubi:web-Feedback in der App gepflegt) ──────────────

    /** Update branding for a company */
    async updateBranding(companyId: number, data: Partial<Branding>): Promise<{ success: boolean; branding?: Branding; error?: string }> {
        try {
            const token = localStorage.getItem('token');
            if (!token) throw new Error('No token available');

            const response = await fetch(`${SSO_URL}/api/branding/${companyId}`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            });
            const result = await response.json();
            if (!response.ok) {
                return { success: false, error: result.error || 'Update failed' };
            }
            return { success: true, branding: result.branding };
        } catch (error) {
            console.error('Error updating branding:', error);
            return { success: false, error: 'Network error' };
        }
    }

    private async uploadAsset(companyId: number, endpoint: string, field: string, file: File): Promise<{ success: boolean; error?: string; [key: string]: any }> {
        try {
            const token = localStorage.getItem('token');
            if (!token) throw new Error('No token available');

            const formData = new FormData();
            formData.append(field, file);

            const response = await fetch(`${SSO_URL}/api/branding/${companyId}/${endpoint}`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData,
            });
            const result = await response.json();
            if (!response.ok) {
                return { success: false, error: result.error || 'Upload failed' };
            }
            return { success: true, ...result };
        } catch (error) {
            console.error(`Error uploading ${field}:`, error);
            return { success: false, error: 'Network error' };
        }
    }

    /** Upload logo for a company */
    uploadLogo(companyId: number, file: File): Promise<{ success: boolean; logoUrl?: string; error?: string }> {
        return this.uploadAsset(companyId, 'logo', 'logo', file);
    }

    /** Upload favicon for a company */
    uploadFavicon(companyId: number, file: File): Promise<{ success: boolean; faviconUrl?: string; error?: string }> {
        return this.uploadAsset(companyId, 'favicon', 'favicon', file);
    }

    /** Upload login background for a company */
    uploadBackground(companyId: number, file: File): Promise<{ success: boolean; backgroundUrl?: string; error?: string }> {
        return this.uploadAsset(companyId, 'background', 'background', file);
    }
}

export const brandingService = new BrandingService();
